import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import type {
  DepartmentPropagationImpact,
  DepartmentPropagationResult,
  Uuid,
  VerticalDepartment,
} from "@tilt/admin-sdk";

/**
 * Department propagation prompt (#154).
 *
 * Fires after super_admin successfully adds a department to a
 * vertical catalog. The cashier-side picker already surfaces the
 * new dept via the unconditional UNION ALL on the POS lookups, so
 * locations / orgs that have NOT curated their dept subset get the
 * new option for free. But scopes that explicitly curated rows in
 * ``organization_vertical_department_tbl`` /
 * ``location_department_tbl`` will be missing the new dept until
 * an admin propagates it.
 *
 * UX flow:
 *   1. Open → fetch preflight impact counts.
 *   2. If both ``orgs_to_propagate`` and ``locations_to_propagate``
 *      are zero, render a "nothing to propagate" body with a Done
 *      button and skip the form.
 *   3. Otherwise show two checkboxes (org tier, location tier),
 *      each toggled by default when its count > 0. Confirm runs
 *      the POST against the resolved scope (org / location / both).
 *   4. On success show the row counts that were actually inserted
 *      and a Done button. Idempotent server-side, so re-running
 *      after a partial failure is safe.
 */
export interface PropagateDepartmentDialogProps {
  readonly verticalId: Uuid;
  readonly verticalName: string;
  readonly department: VerticalDepartment;
  readonly onClose: () => void;
}

export function PropagateDepartmentDialog({
  verticalId,
  verticalName,
  department,
  onClose,
}: PropagateDepartmentDialogProps): JSX.Element {
  const impact = useQuery<DepartmentPropagationImpact>({
    queryKey: ["dept-propagation-impact", verticalId, department.id],
    queryFn: () => apiClient.getDepartmentPropagationImpact(verticalId, department.id),
    retry: false,
    staleTime: 0,
  });

  const [includeOrgs, setIncludeOrgs] = useState(true);
  const [includeLocations, setIncludeLocations] = useState(true);
  const [completed, setCompleted] = useState<DepartmentPropagationResult | null>(null);

  const propagate = useMutation({
    mutationFn: (scope: "org" | "location" | "both") =>
      apiClient.propagateDepartment(verticalId, department.id, { scope }),
    onSuccess: (result) => setCompleted(result),
  });

  // Initial loading
  if (impact.isLoading) {
    return (
      <Shell title={department.display_name} onClose={onClose}>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking which orgs and locations would
          receive this department…
        </p>
      </Shell>
    );
  }

  if (impact.isError || !impact.data) {
    return (
      <Shell title={department.display_name} onClose={onClose}>
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {describe(impact.error)}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border bg-background px-3 py-1.5 text-sm hover:bg-muted"
          >
            Close
          </button>
        </div>
      </Shell>
    );
  }

  const { orgs_to_propagate, locations_to_propagate, orgs_curated, locations_curated } =
    impact.data;
  const nothingToPropagate = orgs_to_propagate === 0 && locations_to_propagate === 0;

  // After successful propagation
  if (completed) {
    return (
      <Shell title={department.display_name} onClose={onClose}>
        <p className="text-sm">
          Added <strong>{department.display_name}</strong> to{" "}
          <strong>{completed.orgs_added}</strong>{" "}
          {completed.orgs_added === 1 ? "organization" : "organizations"} and{" "}
          <strong>{completed.locations_added}</strong>{" "}
          {completed.locations_added === 1 ? "location" : "locations"}.
        </p>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
          >
            Done
          </button>
        </div>
      </Shell>
    );
  }

  // Nothing to propagate - friendly empty state
  if (nothingToPropagate) {
    return (
      <Shell title={department.display_name} onClose={onClose}>
        <p className="text-sm text-muted-foreground">
          No curated organizations or locations on the {verticalName} vertical need this dept added
          - they'll see {department.display_name} automatically through the catalog.
        </p>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
          >
            Got it
          </button>
        </div>
      </Shell>
    );
  }

  // Default - render the choice form
  const resolvedScope: "org" | "location" | "both" =
    includeOrgs && includeLocations ? "both" : includeOrgs ? "org" : "location";
  const submitDisabled =
    propagate.isPending ||
    (!includeOrgs && !includeLocations) ||
    (includeOrgs && orgs_to_propagate === 0 && !includeLocations) ||
    (includeLocations && locations_to_propagate === 0 && !includeOrgs);

  return (
    <Shell title={department.display_name} onClose={onClose}>
      <p className="text-sm">
        <strong>{department.display_name}</strong> was added to {verticalName}. Some scopes have
        explicitly curated their department list - apply the new dept to them too?
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Uncurated scopes ({impact.data.orgs_total - orgs_curated} orgs and{" "}
        {impact.data.locations_total - locations_curated} locations) will see this dept
        automatically - no action needed.
      </p>

      <div className="mt-3 space-y-2 rounded-md border bg-muted/30 p-3">
        <Toggle
          label={`Apply to ${orgs_to_propagate} curated organization${orgs_to_propagate === 1 ? "" : "s"}`}
          subtitle={
            orgs_to_propagate === 0
              ? "All curated orgs already have this dept."
              : `${orgs_curated} orgs have curated lists on this vertical.`
          }
          checked={includeOrgs}
          onChange={setIncludeOrgs}
          disabled={orgs_to_propagate === 0}
        />
        <Toggle
          label={`Apply to ${locations_to_propagate} curated location${locations_to_propagate === 1 ? "" : "s"}`}
          subtitle={
            locations_to_propagate === 0
              ? "All curated locations already have this dept."
              : `${locations_curated} locations have curated lists on this vertical.`
          }
          checked={includeLocations}
          onChange={setIncludeLocations}
          disabled={locations_to_propagate === 0}
        />
      </div>

      {propagate.isError && (
        <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          {describe(propagate.error)}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={propagate.isPending}
          className="rounded-md border bg-background px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={() => propagate.mutate(resolvedScope)}
          disabled={submitDisabled}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {propagate.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Apply
        </button>
      </div>
    </Shell>
  );
}

function Shell({
  title,
  onClose,
  children,
}: {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-card p-5 shadow-xl">
        <header className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Propagate department
            </p>
            <h2 className="mt-0.5 text-base font-semibold">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function Toggle({
  label,
  subtitle,
  checked,
  onChange,
  disabled,
}: {
  readonly label: string;
  readonly subtitle: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly disabled?: boolean;
}): JSX.Element {
  return (
    <label className={`flex items-start gap-2 text-sm ${disabled ? "opacity-50" : ""}`}>
      <input
        type="checkbox"
        checked={checked && !disabled}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        aria-label={label}
        className="mt-0.5"
      />
      <span>
        <span className="font-medium">{label}</span>
        <span className="ml-2 text-xs text-muted-foreground">{subtitle}</span>
      </span>
    </label>
  );
}
